import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { WalletError } from '@cef-ai/wallet-api-client';
import { useWallet, walletErrorMessage } from '~/wallet';
import { EmailStep } from '~/routes/Register/EmailStep';
import { OtpStep } from '~/routes/Register/OtpStep';
import { registerErrorMessage } from '~/routes/Register/registerErrorMessage';
import type { PendingRegistrationStore, RegistrationRequest } from '~/stores';
import { Button, ErrorText, Spinner } from '~/ui/primitives';

/** Render a host origin as a compact, readable chip (host only). Mirrors `ConsentPrompt`. */
function OriginChip({ origin }: { origin: string }) {
  let display = origin;
  try {
    display = new URL(origin).host || origin;
  } catch {
    /* keep raw */
  }
  return (
    <span style={originChipStyle} title={origin}>
      {display}
    </span>
  );
}

type Step = 'email' | 'otp' | 'passkey' | 'already-registered';

/**
 * The standard focusable set, for the dialog's Tab ring.
 *
 * Deliberately broader than what these four steps render (fix round 2). Today
 * every control is a `Field` or a `Button`, so `input`/`button` alone would
 * match all of them — but a step that later adds a link, a `select`, a
 * `textarea` or anything carrying `tabindex` would drop out of the trap
 * SILENTLY, with no test failing, because a narrow selector cannot tell "no
 * such element" from "element I refuse to see". A guard that quietly stops
 * guarding is the failure mode this branch keeps hitting, so the selector
 * covers the set the browser itself considers tabbable rather than the set
 * currently on screen.
 *
 * `:not([disabled])` skips controls the step has turned off while busy;
 * `[tabindex]:not([tabindex="-1"])` picks up deliberately-focusable
 * non-controls while excluding programmatic-only focus targets.
 *
 * Exported so `__tests__/RegisterPrompt.test.tsx` can walk the same ring the
 * trap walks. The test's subject is the CYCLING (forward, backward, never
 * escaping), not the membership rule — re-typing the selector there would just
 * create a second copy to drift.
 */
export const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * The email → OTP → passkey flow, run inside the `/embed/wallet` POPUP for a
 * host that called `EmbedWallet.register()`.
 *
 * ## Why this exists at all
 *
 * S0 gates registration on a verified mailbox: `Identity.register()` requires
 * an `emailToken`, and only the wallet origin can mint one (the host cannot —
 * it is issued to whoever answered the code). Until now the only surface that
 * ran those steps was the wallet's own `/register` route, so the embedded
 * intent could do nothing but fail by name. This is the second surface.
 *
 * It is NOT a stopgap. Moving login and signing into the cross-origin iframe
 * (a later sub-project) cannot take registration with it: Safari permanently
 * blocks WebAuthn `create()` in a cross-origin iframe, so a popup is where
 * registration lives for good, and a popup that collects an email is what it
 * has to be.
 *
 * ## The order, and why it is the order
 *
 * `POST /v1/accounts/email/start` answers identically for a bound and an
 * unbound address, so step 1 discloses nothing about who has a wallet. Only
 * `email/verify` — answering someone who has just proven mailbox control —
 * may say `already-registered`. Verifying before the ceremony also means
 * abandoning halfway leaves an expired verification row and nothing else: no
 * half-created account, no orphan credential. Identical to `/register`, on
 * purpose.
 *
 * ## How the message handler and this view meet
 *
 * `createHelloHandler` is a message handler with nothing to render, so it
 * hands off: it calls `PendingRegistrationStore.request()` and awaits the
 * outcome, exactly as the delegation handler already awaits
 * `PendingConsentStore`. This view resolves that promise EXACTLY ONCE, and
 * only with:
 *
 *   - `completed` — a session now exists on this identity (a wallet was
 *     created, or the reader signed in to the one the address already had).
 *     The handler then replies `wallet:login:ok`. Task 24: which of those two
 *     it was rides along as `accountCreated`, because THIS component is the
 *     only place that knows — it ran one branch or the other — and the host
 *     cannot work it out afterwards without racing a concurrent session.
 *   - `cancelled` — the reader stopped. The handler throws
 *     `WalletError('user-cancelled')`, which `PopupHostBridge` turns into a
 *     named `wallet:error`.
 *
 * There is no third outcome, and in particular no "failed": a failure mid-flow
 * leaves the reader on the step whose button can recover it (a fresh code, a
 * retried ceremony, a different address, sign in instead) rather than
 * collapsing the host's call. That is why `identity.register()` runs HERE and
 * not in the handler — the retry affordances and the failure that needs them
 * have to live in the same place, the way they already do on `/register`.
 *
 * ## Copy rule
 *
 * Every sentence rendered here may only name an action that exists on THIS
 * surface. That is why `registerErrorMessage` is reused rather than reworded:
 * it is written against `/register`'s affordances, and this popup deliberately
 * offers the same set — send a code, resend it, use a different address, retry
 * the ceremony, sign in instead — so each message stays true. The one addition
 * `/register` does not need is Cancel, because here a host page is waiting on
 * the other end of a `postMessage`.
 */
export const RegisterPrompt = observer(({ store }: { store: PendingRegistrationStore }) => {
  const p = store.pending;
  if (!p) return null;
  return (
    <RegistrationFlow
      key={p.id}
      request={p.request}
      onCompleted={(accountCreated) => store.resolveCurrent({ kind: 'completed', accountCreated })}
      onCancelled={() => store.resolveCurrent({ kind: 'cancelled' })}
    />
  );
});

/**
 * The dialog and the step machine inside it, remounted per prompt via the `key`
 * above so a second registration in the same popup never inherits the first
 * one's inputs.
 *
 * State is local `useState`, matching `/register` and the Settings bind flow:
 * nothing outside this component observes the step, and the machine dies with
 * the prompt.
 *
 * ## Dialog behaviour (fix round 1)
 *
 * `role="dialog" aria-modal="true"` is a promise about behaviour, not a label.
 * The first version made the declaration and kept none of it: no initial
 * focus (so focus stayed on `<body>` and the dialog was never announced), no
 * Tab containment, and an `aria-label` of "SCP Wallet" — the product, not the
 * task. Its sibling `ConsentPrompt` does all three. This surface is permanent
 * (WebAuthn `create()` is blocked in cross-origin iframes on Safari, so
 * registration lives in a popup for good), so it is the last one that should
 * skip it.
 *
 * Three differences from `ConsentPrompt`, each deliberate:
 *
 *  - **Focus is declarative, not an effect.** Each step's own primary control
 *    carries `autoFocus`, so React focuses it as that control MOUNTS — which
 *    is what makes focus follow the step machine (address → code → the button
 *    that appears when a ceremony fails) without a `[step]` effect racing the
 *    render it depends on.
 *  - **The Tab ring is queried at keypress, not captured in refs.** The
 *    control set here changes with the step and with `busy`; a fixed list of
 *    refs — right for `ConsentPrompt`'s two or three fixed buttons — would go
 *    stale on every transition and silently trap focus on a button that is no
 *    longer rendered.
 *  - **Escape does NOT cancel**, unlike `ConsentPrompt` where it maps to deny.
 *    The two are not analogous: there, Escape declines a one-click decision
 *    with nothing typed. Here it would discard a half-entered address or a
 *    partly-typed six-digit code AND fail the host's call, on a keystroke
 *    people press reflexively. Cancel is an explicit, visible button on every
 *    step instead, and a closed window is covered by the transport's
 *    `popup-closed` detection.
 */
function RegistrationFlow({
  request,
  onCompleted,
  onCancelled,
}: {
  request: RegistrationRequest;
  /**
   * Task 24. `accountCreated` is `true` only from `runCeremony`'s success —
   * the one path here that mints a wallet — and `false` from `runSignIn`,
   * which signs in to one that already existed. Passing it as an argument
   * rather than reading a piece of component state at the call site is
   * deliberate: the two callers are two distinct code paths, so the value is
   * a property of the path, and nothing can drift out of step with it.
   */
  onCompleted: (accountCreated: boolean) => void;
  onCancelled: () => void;
}) {
  const { identity, apiClient } = useWallet();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<Step>('email');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailToken, setEmailToken] = useState<string | null>(null);
  /**
   * The address `email/verify` attested, captured then rather than re-read
   * from the `email` box later, so the passkey the reader ends up with and the
   * address this step told them was confirmed can never diverge. Cleared
   * wherever `emailToken` is.
   */
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  /** The live OTP lifetime from `email/start` — see `OtpStep`'s `expiryPhrase`. */
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  /**
   * Keep Tab inside the dialog. The popup has nothing else on it worth
   * reaching — the route's "Waiting for host" status is behind an opaque
   * overlay — so escaping the ring lands a keyboard reader on the browser
   * chrome with no way back in.
   *
   * The ring is read from the DOM on each keypress rather than captured once:
   * this dialog's controls change with the step AND with `busy` (the primary
   * button is replaced by a spinner during a ceremony, and the tertiary
   * buttons disable), so anything captured at mount would be describing a
   * screen that is no longer there. `:not([disabled])` is what makes the
   * busy state correct for free.
   *
   * The selector is `FOCUSABLE` — the browser's own tabbable set, not the two
   * element types these steps happen to use. See its doc for why.
   */
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    const onKey = (e: KeyboardEvent) => {
      // Leave modifier combos and IME composition to the browser.
      if (e.key !== 'Tab' || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (!node.contains(document.activeElement)) return;
      const ring = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (ring.length === 0) return;
      const idx = ring.indexOf(document.activeElement as HTMLElement);
      e.preventDefault();
      if (idx === -1) {
        ring[0].focus();
        return;
      }
      const next = e.shiftKey ? (idx === 0 ? ring.length - 1 : idx - 1) : idx === ring.length - 1 ? 0 : idx + 1;
      ring[next].focus();
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, []);

  /** Same default as `/register`: the email's local part, still editable above. */
  function displayName(): string {
    return name.trim() || email.trim().split('@')[0];
  }

  async function sendCode() {
    setError(null);
    setBusy(true);
    try {
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setCode('');
      setStep('otp');
    } catch (e) {
      setError(registerErrorMessage(e, 'email'));
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(null);
    setResent(false);
    setResending(true);
    try {
      const { expiresInSeconds: ttl } = await apiClient.accounts.emailStart(email.trim());
      setExpiresInSeconds(ttl ?? null);
      setResent(true);
    } catch (e) {
      setError(registerErrorMessage(e, 'email'));
    } finally {
      setResending(false);
    }
  }

  async function verifyCode() {
    setError(null);
    // Clear the resend acknowledgement: after a resend followed by a failed
    // verify, "Code sent again" would still be describing an action two steps
    // in the past.
    setResent(false);
    setBusy(true);
    let verified: { emailToken: string; status: 'verified' | 'already-registered' };
    try {
      verified = await apiClient.accounts.emailVerify(email.trim(), code.trim());
    } catch (e) {
      setError(registerErrorMessage(e, 'otp'));
      setBusy(false);
      return;
    }
    setEmailToken(verified.emailToken);
    setVerifiedEmail(email.trim());
    if (verified.status === 'already-registered') {
      // The address belongs to an existing account. Registering again would
      // mint a SECOND wallet at a different address, which is not what the
      // host asked for and not what the reader wants — offer sign-in, which
      // completes the host's call with the wallet they already have.
      setStep('already-registered');
      setBusy(false);
      return;
    }
    setStep('passkey');
    // Chained off the same click deliberately: WebAuthn `create()` needs user
    // activation, and one awaited request is well inside the activation
    // window. If it is lost anyway, the passkey step is left showing a
    // "Create passkey" button, which is a fresh, real click.
    await runCeremony(verified.emailToken, email.trim());
  }

  async function runCeremony(token: string, address: string) {
    setError(null);
    setBusy(true);
    try {
      await identity.register({
        emailToken: token,
        displayName: displayName(),
        // The WebAuthn `user.name` hint — what a passkey manager shows as the
        // account. Supplied even when the host sent a `label`: the label is the
        // host's word for this device, the address identifies the account
        // (CEF-AI/wallet#72).
        email: address,
        ...(request.label ? { label: request.label } : {}),
      });
      // A session exists now. Resolving `completed` lets the awaiting handler
      // read `identity.addresses`/`credentialId` and reply `wallet:login:ok`;
      // this component unmounts with the prompt, so nothing is set after it.
      // `true`: this is the path that CREATED the account.
      onCompleted(true);
    } catch (e) {
      // The emailToken is single-use, and `passkey/register/start` may already
      // have spent it before the ceremony failed — retrying with the SAME
      // token can then only fail again. Whenever a post-verification step
      // reports `email-unverified`, treat the token as spent, drop it, and go
      // back for a fresh code. `registerErrorMessage`'s copy for that code
      // names exactly that ("Enter your address again…"), which is only true
      // because of this branch.
      if (e instanceof WalletError && e.code === 'email-unverified') {
        setEmailToken(null);
        setVerifiedEmail(null);
        setCode('');
        setResent(false);
        setStep('email');
        setError(registerErrorMessage(e, 'passkey'));
        setBusy(false);
        return;
      }
      setError(registerErrorMessage(e, 'passkey'));
      setBusy(false);
    }
  }

  /**
   * Sign in to the wallet the address already has, from inside this popup.
   *
   * The host called `register()`, but "you already have one" is not a failure
   * the host can do anything better with than the reader can right here: the
   * popup is open, the click is a real user activation, and a successful
   * ceremony gives the host precisely the `wallet:login:ok` it was waiting
   * for. Without this the reader's only exits would be a different address or
   * cancelling — and "Sign in instead", which several error messages name,
   * would be a phantom.
   *
   * Task 24: this is the path that resolves `accountCreated: false`. It is the
   * whole reason the flag exists — the host asked for a wallet and got a
   * session, but nothing was created, so a first-run tour or a `signup`
   * analytics event must not fire.
   */
  async function runSignIn() {
    setError(null);
    setBusy(true);
    try {
      await identity.login();
      onCompleted(false);
    } catch (e) {
      setError(walletErrorMessage(e));
      setBusy(false);
    }
  }

  function backToEmail() {
    setError(null);
    setCode('');
    setEmailToken(null);
    setVerifiedEmail(null);
    setResent(false);
    setStep('email');
  }

  /**
   * The dialog shell every step renders inside.
   *
   * One element in one position, so React keeps the SAME DOM node across step
   * transitions — which is what lets the Tab-trap effect above hold `[]` deps
   * and still be listening on the node the current step is rendered into.
   *
   * `aria-labelledby` points at whichever step is mounted (each sets `id` on
   * its own heading), so the dialog's accessible name is always the sentence
   * on screen: "Create a new SCP Wallet", "Check your inbox", "Create your
   * passkey", "You already have a wallet".
   */
  const shell = (body: ReactNode) => (
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} style={modalStyle}>
      <div style={cardStyle}>{body}</div>
    </div>
  );

  /**
   * Reachable from every step that can show a message naming it, so
   * "Sign in instead" is never advice without a button. `/register` puts the
   * same offer under its email step; this surface has no `/login` route to
   * link to, so the ceremony runs here.
   */
  const signInFooter = (
    <p style={footerStyle}>
      Already have a wallet?{' '}
      <Button variant="tertiary" size="sm" fullWidth={false} onClick={runSignIn} disabled={busy}>
        Sign in
      </Button>
    </p>
  );

  if (step === 'email') {
    return shell(
      <>
        <EmailStep
          headingId={titleId}
          autoFocus
          description={
            <>
              <OriginChip origin={request.hostOrigin} /> asked for a wallet. We&apos;ll email you a code to confirm the
              address, then secure the wallet with this device&apos;s passkey.
            </>
          }
          name={name}
          email={email}
          onNameChange={setName}
          onEmailChange={setEmail}
          onSubmit={sendCode}
          onCancel={onCancelled}
          busy={busy}
          error={error}
        />
        {signInFooter}
      </>,
    );
  }

  if (step === 'otp') {
    return shell(
      <>
        <OtpStep
          headingId={titleId}
          autoFocus
          email={email.trim()}
          expiresInSeconds={expiresInSeconds}
          code={code}
          onCodeChange={setCode}
          onVerify={verifyCode}
          onResend={resendCode}
          onBack={backToEmail}
          onCancel={onCancelled}
          busy={busy}
          resending={resending}
          resent={resent}
          error={error}
        />
        {signInFooter}
      </>,
    );
  }

  if (step === 'already-registered') {
    return shell(
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <h1 id={titleId} style={titleStyle}>
            You already have a wallet
          </h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {email.trim()} is already registered. Sign in with its passkey — creating another wallet would give you a
            second, separate set of addresses.
          </p>
        </div>
        {busy ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            <Spinner /> Waiting for your device…
          </p>
        ) : (
          // `autoFocus`: this button mounts as the step appears, so React
          // focuses it then — which is both the dialog's initial focus and,
          // after a failed ceremony, focus on the control that recovers it.
          <Button autoFocus onClick={runSignIn}>
            Sign in
          </Button>
        )}
        <Button variant="tertiary" size="sm" onClick={backToEmail} disabled={busy}>
          Use a different address
        </Button>
        <Button variant="tertiary" size="sm" onClick={onCancelled} disabled={busy}>
          Cancel
        </Button>
        {error && <ErrorText>{error}</ErrorText>}
      </div>,
    );
  }

  return shell(
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <h1 id={titleId} style={titleStyle}>
            Create your passkey
          </h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            {email.trim()} is confirmed. Your device will now create the passkey that secures this wallet.
          </p>
        </div>
        {busy ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            <Spinner /> Waiting for your device…
          </p>
        ) : (
          // Retry only re-runs the ceremony: the code is spent, and the token
          // we already hold is what proves the mailbox. Only reachable while
          // that token is still good — a failure reporting `email-unverified`
          // (a spent token) sends the reader back to the address step instead.
          <Button
            autoFocus
            onClick={() => emailToken && verifiedEmail && runCeremony(emailToken, verifiedEmail)}
            disabled={!emailToken || !verifiedEmail}
          >
            Create passkey
          </Button>
        )}
        <Button variant="tertiary" size="sm" onClick={backToEmail} disabled={busy}>
          Use a different address
        </Button>
        <Button variant="tertiary" size="sm" onClick={onCancelled} disabled={busy}>
          Cancel
        </Button>
        {error && <ErrorText>{error}</ErrorText>}
      </div>
      {signInFooter}
    </>,
  );
}

const modalStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--bg-default)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-5)',
  overflowY: 'auto',
  fontFamily: 'var(--font-sans)',
  zIndex: 1000,
};

const cardStyle: CSSProperties = {
  background: 'var(--surface-contrast)',
  borderRadius: 'var(--radius-xl)',
  padding: 'var(--space-6)',
  maxWidth: 420,
  width: '100%',
  boxShadow: 'var(--shadow-xl)',
  border: '1px solid var(--stroke-on-low)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  font: '600 24px/1.25 var(--font-display)',
  letterSpacing: '-0.01em',
};

const originChipStyle: CSSProperties = {
  display: 'inline-block',
  font: '500 13px/1 var(--font-mono)',
  color: 'var(--c-violet-dark)',
  background: 'var(--c-violet-light)',
  borderRadius: 'var(--radius-sm)',
  padding: '3px 8px',
};

const footerStyle: CSSProperties = {
  margin: 'var(--space-4) 0 0',
  textAlign: 'center',
  color: 'var(--text-secondary)',
};
